import { ApiRoutes } from "@/lib/routeResolver";
import { cache } from "react";
import api from "./api";

// ─── Type Definitions ────────────────────────────────────────────────

export interface Article {
  id: string;
  title: string;
  companyId: string;
  destinationId?: string;
  isActive: boolean;
  createdDate: string;
  pageSEO: {
    pageURL: string;
    /** CMS SEO fields. Present on the API but often empty — always fall back. */
    pageTitle?: string;
    pageDescription?: string;
    pageKeywords?: string;
    /** Marketing headline used as the page's <h1> when set. */
    head?: string;
  };
  pageContent: {
    title: string;
    headerPhoto: string;
    description: string;
  };
}

interface ArticleApiResponse {
  pageNumber: number;
  pageSize: number;
  count: number;
  totalPages: number;
  succeeded: boolean;
  data: Article[];
}

export interface DestinationWithArticles {
  id: string;
  nameCountry: string;
}

interface DestinationArticlesApiResponse {
  data: DestinationWithArticles[];
}

// ─── Helpers ─────────────────────────────────────────────────────────

export { stripHtml, truncate } from "./helpers";
import { normalizeName } from "./helpers";

/**
 * Resolve a country name (e.g. from a ?country URL param) to its destination id
 * by normalized comparison — case- and whitespace-insensitive — returning the
 * first match. Empty string when there's no name or no match, which callers
 * treat as "no destination filter".
 */
export function resolveDestinationId(
  destinations: DestinationWithArticles[],
  countryName: string | undefined,
): string {
  const target = normalizeName(countryName);
  if (!target) return "";
  return (
    destinations.find((d) => normalizeName(d.nameCountry) === target)?.id ?? ""
  );
}

export function formatArticleDate(dateStr: string): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// ─── Data Fetching ───────────────────────────────────────────────────

export const fetchArticles = cache(
  async (
    page = 1,
    pageSize = 6,
    destinationId?: string,
  ): Promise<{ articles: Article[]; totalPages: number; count: number }> => {
    const res = await api.get<ArticleApiResponse>(ApiRoutes.WEBSITE_ARTICLE, {
      params: {
        PageNumber: page,
        PageSize: pageSize,
        Status: "Active",
        ...(destinationId ? { DestinationId: destinationId } : {}),
      },
    });

    const articles = (res.data?.data ?? []).filter((a) => a.isActive);
    return {
      articles,
      totalPages: res.data?.totalPages ?? 1,
      count: res.data?.count ?? 0,
    };
  },
);

export const fetchDestinationsWithArticles = cache(
  async (): Promise<DestinationWithArticles[]> => {
    const res = await api.get<DestinationArticlesApiResponse>(
      "website/article/getdestinationwithtravelarticles",
      { params: { PageNumber: 1, PageSize: 100000 } },
    );
    return res.data?.data ?? [];
  },
);

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export async function fetchArticlesClient(
  page = 1,
  pageSize = 6,
  destinationId?: string,
): Promise<{ articles: Article[]; totalPages: number; count: number }> {
  const params = new URLSearchParams({
    PageNumber: String(page),
    PageSize: String(pageSize),
    CompanyId: COMPANY_ID,
    Status: "Active",
  });
  if (destinationId) params.set("DestinationId", destinationId);

  const res = await fetch(`${BASE_URL}/website/article?${params}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN}`,
    },
  });
  const json = await res.json();
  const articles: Article[] = (json.data ?? []).filter(
    (a: Article) => a.isActive,
  );
  return {
    articles,
    totalPages: json.totalPages ?? 1,
    count: json.count ?? 0,
  };
}

export async function fetchDestinationsWithArticlesClient(): Promise<
  DestinationWithArticles[]
> {
  const params = new URLSearchParams({
    PageNumber: "1",
    PageSize: "100000",
    CompanyId: COMPANY_ID,
  });

  const res = await fetch(
    `${BASE_URL}/website/article/getdestinationwithtravelarticles?${params}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${TOKEN}`,
      },
    },
  );
  const json = await res.json();
  return json.data ?? [];
}
